import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Validate environment variables
const validateEnvironment = () => {
  const missing = []
  
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL')
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY')
  
  if (missing.length > 0) {
    console.error('Missing environment variables:', missing.join(', '))
    console.error('This will prevent authentication from working properly')
    return false
  }
  
  console.log('Environment validation passed')
  return true
}

// Run validation
validateEnvironment()

// Create the default Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'X-Client-Info': 'kiracar-web@1.0.0',
      'Accept': 'application/json'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Enhanced authentication with timeout and remember me functionality
export const authenticateWithTimeout = async (
  email: string, 
  password: string, 
  timeoutMs = 15000,
  rememberMe = true
) => {
  console.log('[Auth] Starting authentication with timeout:', timeoutMs)
  console.log('[Auth] Session persistence:', rememberMe ? 'local (persistent)' : 'session (temporary)')
  
  try {
    // Use the main supabase client for authentication
    const authPromise = supabase.auth.signInWithPassword({
      email,
      password
    })
    
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Authentication timeout')), timeoutMs)
    )
    
    const result = await Promise.race([authPromise, timeoutPromise]) as any
    
    // Store the remember me preference for session management
    if (typeof window !== 'undefined') {
      if (rememberMe) {
        // Store preference to keep session persistent
        localStorage.setItem('kiracar-remember-me', 'true')
        sessionStorage.removeItem('kiracar-session-mode')
      } else {
        // Mark session as temporary - will be cleared on browser close
        localStorage.removeItem('kiracar-remember-me')
        sessionStorage.setItem('kiracar-session-mode', 'temporary')
      }
    }
    
    console.log('[Auth] Authentication completed successfully with', rememberMe ? 'persistent' : 'temporary', 'session')
    return result
  } catch (error) {
    console.error('[Auth] Authentication failed:', error.message)
    throw error
  }
}

// Enhanced profile fetch with timeout
export const fetchProfileWithTimeout = async (userId: string, timeoutMs = 10000) => {
  console.log('[Auth] Fetching profile with timeout:', timeoutMs)
  
  try {
    const profilePromise = supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Profile fetch timeout')), timeoutMs)
    )
    
    const result = await Promise.race([profilePromise, timeoutPromise]) as any
    console.log('[Auth] Profile fetched successfully')
    return result
  } catch (error) {
    console.error('[Auth] Profile fetch failed:', error.message)
    throw error
  }
}

// Function to create a fresh Supabase client (for recovery)
export const createFreshSupabaseClient = () => {
  console.log('[Supabase] Creating fresh client instance')
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: false, // Don't persist session for fresh client
      detectSessionInUrl: false
    },
    db: {
      schema: 'public'
    },
    global: {
      headers: {
        'X-Client-Info': 'kiracar-web-fresh@1.0.0',
        'Accept': 'application/json'
      }
    }
  })
}

// Interface for HTTP fetch options
interface HttpFetchOptions {
  select?: string;
  filters?: Record<string, any>;
  order?: { column: string; ascending: boolean } | null;
  limit?: number | null;
  timeoutMs?: number;
}

// Interface for robust fetch options
interface RobustFetchOptions {
  timeoutMs?: number;
  retryWithFreshClient?: boolean;
  fallbackToHttp?: boolean;
}

// Direct HTTP fetch bypassing Supabase SDK entirely
export const directHttpFetch = async (tableName: string, options: HttpFetchOptions = {}) => {
  const { 
    select = '*', 
    filters = {}, 
    order = null, 
    limit = null,
    timeoutMs = 8000 
  } = options
  
  console.log(`[HTTP] Starting direct HTTP fetch for ${tableName}`)
  
  try {
    // Build the URL
    let url = `${supabaseUrl}/rest/v1/${tableName}?select=${select}`
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      url += `&${key}=eq.${value}`
    })
    
    // Add ordering
    if (order) {
      url += `&order=${order.column}.${order.ascending ? 'asc' : 'desc'}`
    }
    
    // Add limit
    if (limit) {
      url += `&limit=${limit}`
    }
    
    console.log(`[HTTP] Fetching URL: ${url}`)
    
    // Create fetch with timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const data = await response.json()
    console.log(`[HTTP] Direct fetch successful, got ${data.length} records`)
    
    return { data, error: null }
  } catch (error) {
    console.log(`[HTTP] Direct fetch failed:`, error.message)
    return { data: null, error }
  }
}

// Function to test if Supabase client is working
export const testSupabaseClient = async (client = supabase, timeoutMs = 3000) => {
  try {
    console.log('[Supabase] Testing client connection...')
    
    const testPromise = client
      .from('cars')
      .select('id')
      .limit(1)
    
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Client test timeout')), timeoutMs)
    )
    
    const { data, error } = await Promise.race([testPromise, timeoutPromise]) as any
    
    if (error) {
      console.log('[Supabase] Client test failed:', error.message)
      return { working: false, error }
    }
    
    console.log('[Supabase] Client test successful')
    return { working: true }
  } catch (error) {
    console.log('[Supabase] Client test error:', error.message)
    return { working: false, error }
  }
}

// Ultra-robust fetch function with HTTP fallback
export const robustSupabaseFetch = async (tableName: string, query: any, options: RobustFetchOptions = {}) => {
  const { timeoutMs = 8000, retryWithFreshClient = true, fallbackToHttp = true } = options
  
  console.log(`[Supabase] Starting ultra-robust fetch for ${tableName}`)
  
  // Try with the main client first
  try {
    console.log('[Supabase] Attempting with main client')
    const mainPromise = query(supabase)
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Main client timeout')), timeoutMs)
    )
    
    const result = await Promise.race([mainPromise, timeoutPromise])
    console.log('[Supabase] Main client successful')
    return result
  } catch (error) {
    console.log('[Supabase] Main client failed:', error.message)
    
    if (retryWithFreshClient) {
      // Try with a fresh client
      try {
        console.log('[Supabase] Attempting with fresh client')
        const freshClient = createFreshSupabaseClient()
        const freshPromise = query(freshClient)
        const freshTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Fresh client timeout')), timeoutMs)
        )
        
        const result = await Promise.race([freshPromise, freshTimeoutPromise])
        console.log('[Supabase] Fresh client successful')
        return result
      } catch (freshError) {
        console.log('[Supabase] Fresh client also failed:', freshError.message)
        
        if (fallbackToHttp) {
          // Last resort: direct HTTP with no hardcoded filters
          console.log('[Supabase] Falling back to direct HTTP')
          return await directHttpFetch(tableName, {
            select: '*',
            timeoutMs
          })
        }
        
        throw freshError
      }
    }
    
    if (fallbackToHttp) {
      // Direct HTTP fallback with no hardcoded filters
      console.log('[Supabase] Falling back to direct HTTP')
      return await directHttpFetch(tableName, {
        select: '*',
        timeoutMs
      })
    }
    
    throw error
  }
}

// Test database connection
export const testDatabaseConnection = async () => {
  try {
    console.log('Testing database connection...')
    const startTime = performance.now()
    
    // Simple query to test connection
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .limit(1)
    
    const endTime = performance.now()
    const responseTime = Math.round(endTime - startTime)
    
    if (error) {
      console.error('Database connection test failed:', error)
      return { success: false, error, responseTime }
    }
    
    console.log(`Database connection test successful (${responseTime}ms)`)
    return { success: true, responseTime }
  } catch (error) {
    console.error('Database connection test error:', error)
    return { success: false, error }
  }
}

// Re-export all types from the types file for convenience
export * from '../types/database'

// Default export for the supabase client
export default supabase

// Function to ensure fresh authenticated session
export const ensureValidSession = async (retryCount = 0, maxRetries = 3) => {
  try {
    console.log('[Auth] Checking current session... (attempt', retryCount + 1, ')')
    
    // First, try to get the current session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession()
    
    if (sessionError) {
      console.error('[Auth] Session error:', sessionError)
      throw new Error('Failed to get session')
    }
    
    // If no session, retry with delay for fresh signups
    if (!sessionData.session || !sessionData.session.user) {
      if (retryCount < maxRetries) {
        console.log('[Auth] No session found, retrying in 1 second... (attempt', retryCount + 1, 'of', maxRetries + 1, ')')
        await new Promise(resolve => setTimeout(resolve, 1000))
        return ensureValidSession(retryCount + 1, maxRetries)
      }
      console.error('[Auth] No valid session found after', maxRetries + 1, 'attempts')
      throw new Error('No authenticated session')
    }
    
    // Check if token is close to expiry (within 5 minutes)
    const session = sessionData.session
    const expiresAt = session.expires_at * 1000 // Convert to milliseconds
    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000
    
    if (expiresAt - now < fiveMinutes) {
      console.log('[Auth] Token expiring soon, refreshing...')
      
      // Refresh the token
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession()
      
      if (refreshError || !refreshData.session) {
        console.error('[Auth] Failed to refresh session:', refreshError)
        throw new Error('Failed to refresh authentication')
      }
      
      console.log('[Auth] Session refreshed successfully')
      return refreshData.session
    }
    
    console.log('[Auth] Session is valid and fresh')
    return session
  } catch (error) {
    console.error('[Auth] Session validation failed:', error)
    throw error
  }
} 